import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { emisionDteApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/NumberInput';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import { fmtFecha } from '../../lib/utils';
import {
  ArrowLeft, Loader2, Plus, Trash2, CheckCircle2, XCircle, AlertTriangle, Eye, Send,
  FileText, Receipt, Banknote, Paperclip, Download, Clock, History, RefreshCw, ShieldCheck,
  Mail, Upload, Info, Cloud, CloudDownload, Link2, ShieldAlert, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { EstadoBadge, SituacionBadge, EstadoContableBadge, FactoringBadge, OrigenBadge, TIPO_LABEL } from './emisionBadges';

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;

// Primera fecha de emisión permitida (ventana SII): mes en curso; hasta el día 5 del mes
// siguiente aún se admite el mes anterior. Recibe/devuelve 'yyyy-mm-dd'.
const fechaEmisionMinCL = (hoyStr) => {
  const [y, m, d] = hoyStr.split('-').map(Number);
  let yy = y, mm = m;
  if (d <= 5) { mm = m - 1; if (mm < 1) { mm = 12; yy = y - 1; } }
  return `${yy}-${String(mm).padStart(2, '0')}-01`;
};

const parseCondicion = (s) => {
  const t = String(s || '').toLowerCase();
  if (t.includes('contado') || t.includes('efectivo')) return { mode: 'contado', dias: 0 };
  const m = t.match(/(\d+)/);
  return { mode: 'credito', dias: m ? Number(m[1]) : 30 };
};

// Las descargas usan responseType blob: el 'detail' del backend llega como Blob, hay que leerlo.
const leerErrorBlob = async (err, fallback) => {
  try {
    const d = err?.response?.data;
    if (d instanceof Blob) {
      const txt = await d.text();
      try { return JSON.parse(txt).detail || fallback; } catch { return fallback; }
    }
    return d?.detail || fallback;
  } catch { return fallback; }
};

const PASOS = [
  { n: 1, label: 'Datos', icon: FileText },
  { n: 2, label: 'Validación', icon: ShieldCheck },
  { n: 3, label: 'Vista previa', icon: Eye },
  { n: 4, label: 'Confirmar', icon: Send },
];

const EVENTO_RECEPTOR = [
  { value: 'acuse_recibo', label: 'Acuse de recibo' },
  { value: 'recepcion_conforme', label: 'Recepción conforme de mercadería/servicio' },
  { value: 'reclamo', label: 'Reclamo al contenido' },
  { value: 'rechazo', label: 'Rechazo del documento' },
  { value: 'aceptacion_tacita', label: 'Aceptación tácita (8 días)' },
];

const FACTORING_ESTADOS = [
  { value: 'no_cedible', label: 'No cedible' },
  { value: 'disponible', label: 'Cedible / disponible' },
  { value: 'en_evaluacion', label: 'En evaluación' },
  { value: 'cedida', label: 'Cedida' },
  { value: 'financiada', label: 'Financiada' },
  { value: 'liquidada', label: 'Liquidada' },
];

export default function EmisionDTEDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const rol = (user?.rol || '').toLowerCase();
  const esGerencia = ['admin', 'administrador', 'gerencia'].includes(rol);
  const puedeEmitir = esGerencia || rol === 'contabilidad' || Boolean(user?.permisos?.contabilidad?.acciones?.proformas_aprobar);
  const puedeAnular = esGerencia || rol === 'contabilidad' || Boolean(user?.permisos?.contabilidad?.acciones?.anular_dte);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);

  const load = useCallback(async () => {
    try {
      const res = await emisionDteApi.get(id);
      setDoc(res.data);
    } catch {
      toast.error('No se pudo cargar el documento');
      navigate('/emision-dte');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando documento…</div>;
  }
  if (!doc) return null;

  const emitido = doc.emitido;

  return (
    <div className="space-y-4 pb-24" data-testid="emision-dte-detail">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/emision-dte')} data-testid="btn-volver">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Receipt className="w-5 h-5 text-indigo-600" />
              {TIPO_LABEL[doc.tipo_dte] || doc.tipo_dte}
              {doc.folio ? <span className="font-mono text-indigo-700">· Folio {doc.folio}</span> : <span className="text-muted-foreground text-sm font-normal">· sin folio</span>}
            </h1>
            <p className="text-sm text-muted-foreground">{doc.receptor?.razon_social || 'Sin cliente'} · {doc.receptor?.rut || '—'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <EstadoBadge estado={doc.estado} label={doc.estado_label} />
          {doc.situacion_comercial !== 'borrador' && <SituacionBadge situacion={doc.situacion_comercial} />}
          {emitido && <EstadoContableBadge estado={doc.estado_contable} />}
          {emitido && <FactoringBadge factoring={doc.factoring} />}
          <OrigenBadge origen={doc.origen} />
        </div>
      </div>

      {doc.tipo_dte === '61' && (
        <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-[13px] font-semibold text-red-700" data-testid="banner-nota-credito">
          <Ban className="w-4 h-4 mt-0.5 shrink-0" />
          <span>NOTA DE CRÉDITO{doc.ref_folio_origen ? ` — anula la factura folio ${doc.ref_folio_origen}` : ''}. La trazabilidad es por el número de la factura ya emitida.</span>
        </div>
      )}

      {doc.origen === 'externo' && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800" data-testid="banner-externo">
          <CloudDownload className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Documento <b>emitido por fuera</b> (directo en DTECore). Es una copia fiel del XML del SII: <b>solo lectura</b>, no se puede editar ni re-emitir. Solo puedes asociarle una proforma (enlace interno) para contabilizarlo.</span>
        </div>
      )}

      {emitido
        ? <VistaEmitido doc={doc} reload={load} puedeEmitir={puedeEmitir} esGerencia={esGerencia} puedeAnular={puedeAnular} />
        : <Wizard doc={doc} setDoc={setDoc} step={step} setStep={setStep} reload={load} puedeEmitir={puedeEmitir} />}
    </div>
  );
}

/* ───────────────────────── Wizard (borrador) ───────────────────────── */
function Wizard({ doc, setDoc, step, setStep, reload, puedeEmitir }) {
  return (
    <>
      <Stepper step={step} />
      {step === 1 && <PasoDatos doc={doc} setDoc={setDoc} onNext={() => setStep(2)} puedeEmitir={puedeEmitir} />}
      {step === 2 && <PasoValidacion doc={doc} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
      {step === 3 && <PasoPreview doc={doc} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
      {step === 4 && <PasoConfirmar doc={doc} onBack={() => setStep(3)} reload={reload} puedeEmitir={puedeEmitir} />}
    </>
  );
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto" data-testid="wizard-stepper">
      {PASOS.map((p, i) => {
        const Icon = p.icon;
        const done = step > p.n;
        const active = step === p.n;
        return (
          <div key={p.n} className="flex items-center gap-1 sm:gap-2">
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${active ? 'bg-indigo-600 text-white border-indigo-600' : done ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-gray-400 border-gray-200'}`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{p.n}. {p.label}</span>
              <span className="sm:hidden">{p.n}</span>
            </div>
            {i < PASOS.length - 1 && <div className={`h-0.5 w-4 sm:w-8 ${done ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
          </div>
        );
      })}
    </div>
  );
}

/* ── Paso 1: datos ── */
function PasoDatos({ doc, setDoc, onNext, puedeEmitir }) {
  const [items, setItems] = useState(doc.items?.length ? doc.items.map((i) => ({ ...i })) : [{ descripcion: '', cantidad: 1, unidad: 'un', precio_unitario: 0, descuento: 0, exento: false }]);
  const [refs, setRefs] = useState({ orden_compra: '', hes: '', codigo_contrato: '', centro_costo: '', hito_cobro: '', folio_referencia: '', ot_numero: '', ...(doc.referencias || {}) });
  const [obs, setObs] = useState(doc.observaciones || '');
  const [tipoDte, setTipoDte] = useState(doc.tipo_dte || '33');
  const [ivaPct, setIvaPct] = useState(doc.totales?.iva_porcentaje ?? 19);
  const [guia, setGuia] = useState(doc.guia || {});
  const initCond = parseCondicion(doc.condicion_pago || '30 dias');
  const [condMode, setCondMode] = useState(initCond.mode);
  const [condDias, setCondDias] = useState(initCond.dias || 30);
  const [saving, setSaving] = useState(false);
  const hoyCL = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
  const fechaMinCL = fechaEmisionMinCL(hoyCL);
  const [fechaEmision, setFechaEmision] = useState(doc.fecha_emision || hoyCL);

  const esNC = tipoDte === '61';
  const esND = tipoDte === '56';
  const esExenta = tipoDte === '34';
  const esGuia = tipoDte === '52';
  const fromProforma = !!doc.proforma_id;

  const setItem = (idx, campo, val) => setItems(items.map((it, i) => i === idx ? { ...it, [campo]: val } : it));
  const addItem = () => setItems([...items, { descripcion: '', cantidad: 1, unidad: 'un', precio_unitario: 0, descuento: 0, exento: false }]);
  const delItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const lineTotal = (it) => Math.max(0, (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0) - (Number(it.descuento) || 0));
  const neto = items.filter((it) => !it.exento && !esExenta).reduce((s, it) => s + lineTotal(it), 0);
  const exento = items.filter((it) => it.exento || esExenta).reduce((s, it) => s + lineTotal(it), 0);
  const iva = esExenta ? 0 : Math.round(neto * (ivaPct / 100));
  const total = Math.round(neto + exento + iva);

  const guardar = async () => {
    setSaving(true);
    try {
      const body = {
        tipo_dte: tipoDte,
        items: items.map((it) => ({
          descripcion: it.descripcion || '', cantidad: Number(it.cantidad) || 0, unidad: it.unidad || 'un',
          precio_unitario: Number(it.precio_unitario) || 0, descuento: Number(it.descuento) || 0, exento: !!it.exento,
        })),
        referencias: refs,
        observaciones: obs,
        iva_porcentaje: Number(ivaPct) || 0,
        condicion_pago: condMode === 'contado' ? 'Contado' : `${condDias} dias`,
        fecha_emision: fechaEmision || hoyCL,
      };
      if (esGuia) body.guia = guia;
      const res = await emisionDteApi.editar(doc.id, body);
      setDoc(res.data);
      toast.success('Datos guardados');
      onNext();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudieron guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  const rec = doc.receptor || {};
  const faltaReceptor = !rec.rut || !rec.razon_social;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        {/* Receptor */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Receipt className="w-4 h-4 text-indigo-500" /> Receptor (desde el Maestro de Clientes)</CardTitle></CardHeader>
          <CardContent className="text-sm" data-testid="panel-receptor">
            {faltaReceptor ? (
              <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" /> No hay un cliente válido asociado. Vuelve al listado y crea el borrador seleccionando un cliente del maestro.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <Dato k="Razón social" v={rec.razon_social} />
                <Dato k="RUT" v={rec.rut} />
                <Dato k="Giro" v={rec.giro} warn={!rec.giro} />
                <Dato k="Dirección" v={rec.direccion} warn={!rec.direccion} />
                <Dato k="Comuna" v={rec.comuna} warn={!rec.comuna} />
                <Dato k="Ciudad" v={rec.ciudad} />
                <Dato k="Correo" v={rec.correo} warn={!rec.correo} />
                <Dato k="Contacto" v={rec.contacto} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ítems */}
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-500" /> Ítems del documento</CardTitle>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="btn-add-item"><Plus className="w-3.5 h-3.5 mr-1" />Agregar</Button>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="panel-items">
            {items.map((it, idx) => (
              <div key={idx} className="rounded-lg border p-2.5 space-y-2" data-testid={`item-row-${idx}`}>
                <div className="flex gap-2 items-start">
                  <Input placeholder="Descripción del producto o servicio" value={it.descripcion} onChange={(e) => setItem(idx, 'descripcion', e.target.value)} data-testid={`item-desc-${idx}`} className="flex-1" />
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-red-500 shrink-0" onClick={() => delItem(idx)} data-testid={`item-del-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div><Label className="text-[10px]">Cantidad</Label><NumberInput allowNegative={false} value={it.cantidad} onChange={(clean) => setItem(idx, 'cantidad', clean)} data-testid={`item-cant-${idx}`} /></div>
                  <div><Label className="text-[10px]">Unidad</Label><Input value={it.unidad} onChange={(e) => setItem(idx, 'unidad', e.target.value)} /></div>
                  <div><Label className="text-[10px]">Precio unit.</Label><NumberInput allowNegative={false} value={it.precio_unitario} onChange={(clean) => setItem(idx, 'precio_unitario', clean)} data-testid={`item-precio-${idx}`} /></div>
                  <div><Label className="text-[10px]">Descuento</Label><NumberInput allowNegative={false} value={it.descuento} onChange={(clean) => setItem(idx, 'descuento', clean)} /></div>
                  <div className="flex flex-col justify-end">
                    <Label className="text-[10px]">Subtotal</Label>
                    <div className="h-9 flex items-center font-mono font-semibold text-sm">{fmt(lineTotal(it))}</div>
                  </div>
                </div>
                {!esExenta && (
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Checkbox checked={!!it.exento} onCheckedChange={(v) => setItem(idx, 'exento', !!v)} data-testid={`item-exento-${idx}`} /> Ítem exento de IVA
                  </label>
                )}
              </div>
            ))}
            {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Agrega al menos un ítem.</p>}
          </CardContent>
        </Card>

        {/* Referencias */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Info className="w-4 h-4 text-indigo-500" /> Referencias y observaciones</CardTitle></CardHeader>
          <CardContent className="space-y-3" data-testid="panel-referencias">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Campo label="Orden de Compra" v={refs.orden_compra} onChange={(v) => setRefs({ ...refs, orden_compra: v })} testid="ref-oc" />
              <Campo label="N° HES" v={refs.hes} onChange={(v) => setRefs({ ...refs, hes: v })} testid="ref-hes" />
              <Campo label="Código contrato" v={refs.codigo_contrato} onChange={(v) => setRefs({ ...refs, codigo_contrato: v })} locked={fromProforma} hint={fromProforma ? 'Automático desde la proforma' : ''} testid="ref-codigo-contrato" />
              <Campo label="Centro de costo" v={refs.centro_costo} onChange={(v) => setRefs({ ...refs, centro_costo: v })} locked={fromProforma} hint={fromProforma ? 'Descripción de la OT' : ''} testid="ref-centro-costo" />
              <Campo label="Hito de cobro" v={refs.hito_cobro} onChange={(v) => setRefs({ ...refs, hito_cobro: v })} />
              <Campo label="N° OT" v={refs.ot_numero} onChange={(v) => setRefs({ ...refs, ot_numero: v })} />
            </div>
            {(esNC || esND) && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5">
                <Campo label={esNC ? 'Folio de la factura que corrige (obligatorio)' : 'Folio de la factura que modifica (recomendado)'} v={refs.folio_referencia} onChange={(v) => setRefs({ ...refs, folio_referencia: v })} testid="ref-folio-ref" />
              </div>
            )}
            <div>
              <Label className="text-xs">Observaciones</Label>
              <Textarea rows={2} value={obs} onChange={(e) => setObs(e.target.value)} data-testid="ref-observaciones" />
            </div>
          </CardContent>
        </Card>

        {/* Datos de traslado (Guía de Despacho) */}
        {esGuia && (
          <Card data-testid="panel-guia">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Info className="w-4 h-4 text-indigo-500" /> Datos del traslado (Guía de Despacho · Res. 154)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Campo label="RUT transportista" v={guia.rut_transporte} onChange={(v) => setGuia({ ...guia, rut_transporte: v })} testid="guia-rut-transporte" />
                <Campo label="RUT chofer" v={guia.rut_chofer} onChange={(v) => setGuia({ ...guia, rut_chofer: v })} testid="guia-rut-chofer" />
                <Campo label="Nombre chofer" v={guia.nombre_chofer} onChange={(v) => setGuia({ ...guia, nombre_chofer: v })} testid="guia-nombre-chofer" />
                <Campo label="Patente vehículo" v={guia.patente} onChange={(v) => setGuia({ ...guia, patente: v })} testid="guia-patente" />
                <Campo label="Patente carro" v={guia.patente_carro} onChange={(v) => setGuia({ ...guia, patente_carro: v })} />
                <div>
                  <Label className="text-[10px]">Traslado</Label>
                  <select className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={guia.codigo_traslado || 2}
                    onChange={(e) => setGuia({ ...guia, codigo_traslado: Number(e.target.value) })} data-testid="guia-traslado">
                    <option value={1}>Venta (constituye venta)</option>
                    <option value={2}>Hacia instalaciones del cliente</option>
                    <option value={4}>Traslado interno</option>
                    <option value={5}>Otros traslados no venta</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Campo label="Dirección destino" v={guia.direccion_destino} onChange={(v) => setGuia({ ...guia, direccion_destino: v })} testid="guia-direccion-destino" />
                <Campo label="Comuna destino" v={guia.comuna_destino} onChange={(v) => setGuia({ ...guia, comuna_destino: v })} />
                <Campo label="Ciudad destino" v={guia.ciudad_destino} onChange={(v) => setGuia({ ...guia, ciudad_destino: v })} />
                <div><Label className="text-[10px]">Fecha salida</Label><Input type="date" className="h-9" value={guia.fecha_salida || ''} onChange={(e) => setGuia({ ...guia, fecha_salida: e.target.value })} data-testid="guia-fecha-salida" /></div>
                <div><Label className="text-[10px]">Hora salida</Label><Input type="time" className="h-9" value={guia.hora_salida || ''} onChange={(e) => setGuia({ ...guia, hora_salida: e.target.value })} /></div>
                <div><Label className="text-[10px]">Fecha llegada</Label><Input type="date" className="h-9" value={guia.fecha_llegada || ''} onChange={(e) => setGuia({ ...guia, fecha_llegada: e.target.value })} /></div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Resumen lateral */}
      <div className="space-y-4">
        <Card className="sticky top-4">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Resumen</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Tipo de documento</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={tipoDte} onChange={(e) => setTipoDte(e.target.value)} data-testid="datos-tipo-dte">
                <option value="33">Factura Afecta (33)</option>
                <option value="34">Factura Exenta (34)</option>
                <option value="52">Guía de Despacho (52)</option>
                <option value="61">Nota de Crédito (61)</option>
                <option value="56">Nota de Débito (56)</option>
              </select>
            </div>
            <div data-testid="datos-fecha-emision-wrap">
              <Label className="text-xs flex items-center gap-1"><Clock className="w-3 h-3 text-indigo-500" /> Fecha de emisión</Label>
              <Input type="date" min={fechaMinCL} max={hoyCL} value={fechaEmision}
                onChange={(e) => {
                  const v = e.target.value;
                  // Solo acotar cuando la fecha está completa y el año es real (evita que salte a la
                  // fecha mínima mientras se teclea el año dígito a dígito).
                  if (v && v.length === 10 && Number(v.slice(0, 4)) >= 1900) {
                    let c = v;
                    if (c > hoyCL) c = hoyCL;
                    if (c < fechaMinCL) c = fechaMinCL;
                    setFechaEmision(c);
                  } else {
                    setFechaEmision(v);
                  }
                }} data-testid="datos-fecha-emision" />
              <p className="text-[10px] text-muted-foreground mt-1">Usa hoy o una fecha dentro del plazo permitido (mes en curso; hasta el día 5 del mes siguiente aún admite el mes anterior). Define la fecha del documento, su vencimiento y lo enviado al SII.</p>
            </div>
            {!esExenta && (
              <div>
                <Label className="text-xs">% IVA</Label>
                <Input type="number" value={ivaPct} onChange={(e) => setIvaPct(e.target.value)} data-testid="datos-iva" />
              </div>
            )}
            {esGuia ? (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-800" data-testid="datos-condicion-guia">
                Guía de Despacho: se rotula <b>Sólo Traslado</b> (sin condición de pago).
              </div>
            ) : (
              <div data-testid="datos-condicion-pago">
                <Label className="text-xs">Condición de pago</Label>
                <div className="flex gap-2 items-center">
                  <select className="flex h-10 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                    value={condMode} onChange={(e) => setCondMode(e.target.value)} data-testid="datos-condicion-modo">
                    <option value="contado">Contado</option>
                    <option value="credito">Crédito</option>
                  </select>
                  {condMode === 'credito' && (
                    <div className="flex items-center gap-1">
                      <Input type="number" min={1} className="w-20 h-10" value={condDias}
                        onChange={(e) => setCondDias(Number(e.target.value) || 0)} data-testid="datos-condicion-dias" />
                      <span className="text-xs text-muted-foreground">días</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <Separator />
            <Row k="Neto" v={fmt(neto)} />
            {exento > 0 && <Row k="Exento" v={fmt(exento)} />}
            {!esExenta && <Row k={`IVA (${ivaPct}%)`} v={fmt(iva)} />}
            <Separator />
            <Row k="Total" v={fmt(total)} bold />
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={guardar} disabled={saving || !puedeEmitir} data-testid="btn-guardar-continuar">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Guardar y validar
            </Button>
            {!puedeEmitir && <p className="text-[11px] text-amber-700">No tienes permiso para editar documentos de venta.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ── Paso 2: validación ── */
function PasoValidacion({ doc, onBack, onNext }) {
  const [val, setVal] = useState(null);
  const [dups, setDups] = useState([]);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const [v, d] = await Promise.all([emisionDteApi.validar(doc.id), emisionDteApi.duplicados(doc.id)]);
      setVal(v.data);
      setDups(d.data.avisos || []);
    } catch {
      toast.error('No se pudo validar el documento');
    } finally {
      setLoading(false);
    }
  }, [doc.id]);

  useEffect(() => { run(); }, [run]);

  const criticosFallan = val ? val.checks.filter((c) => c.critico && !c.ok) : [];
  const ok = val?.ok;

  return (
    <Card>
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-indigo-500" /> Revisión previa a la emisión</CardTitle>
        <Button size="sm" variant="outline" onClick={run} data-testid="btn-revalidar"><RefreshCw className="w-3.5 h-3.5 mr-1" />Revisar de nuevo</Button>
      </CardHeader>
      <CardContent className="space-y-4" data-testid="panel-validacion">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-6"><Loader2 className="w-4 h-4 animate-spin" /> Revisando…</div>
        ) : (
          <>
            <div className={`rounded-md p-3 text-sm font-medium flex items-center gap-2 ${ok ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="validacion-resultado">
              {ok ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              {ok ? 'Todo en orden: puedes continuar a la vista previa.' : `Faltan datos obligatorios (${criticosFallan.length}). Corrígelos antes de emitir.`}
            </div>

            <div className="space-y-1.5">
              {val?.checks.map((c) => (
                <div key={c.campo} className="flex items-start gap-2 text-sm" data-testid={`check-${c.campo}`}>
                  {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    : c.critico ? <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />}
                  <span className={c.ok ? 'text-gray-700' : c.critico ? 'text-red-700 font-medium' : 'text-amber-700'}>
                    {c.mensaje}{!c.critico && !c.ok ? ' (recomendado, no bloquea)' : ''}
                  </span>
                </div>
              ))}
            </div>

            {dups.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1.5" data-testid="panel-duplicados">
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Posibles duplicados (solo aviso)</p>
                {dups.map((a, i) => <p key={i} className="text-xs text-amber-700">• {a.mensaje}</p>)}
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={onBack} data-testid="btn-val-atras">Atrás</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={onNext} disabled={!ok} data-testid="btn-val-continuar">Continuar a vista previa</Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Paso 3: vista previa no fiscal ── */
function PasoPreview({ doc, onBack, onNext }) {
  const [prev, setPrev] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await emisionDteApi.preview(doc.id); setPrev(r.data); }
      catch { toast.error('No se pudo generar la vista previa'); }
      finally { setLoading(false); }
    })();
  }, [doc.id]);

  if (loading) return <Card><CardContent className="py-10 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Generando vista previa…</CardContent></Card>;

  return (
    <div className="space-y-4">
      <div className="relative" data-testid="panel-preview">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <span className="text-4xl sm:text-6xl font-black text-red-500/10 rotate-[-18deg] select-none text-center leading-tight">VISTA PREVIA<br />NO FISCAL</span>
        </div>
        <Card className="border-2 border-dashed border-indigo-200">
          <CardContent className="p-5">
            <div className="bg-indigo-50 border border-indigo-200 rounded-md p-2 text-xs text-indigo-800 mb-4 flex items-center gap-2">
              <Eye className="w-4 h-4" /> {prev?.aviso_no_fiscal || 'Vista previa NO fiscal — no consume folio ni emite el documento.'}
            </div>
            <ResumenDoc doc={prev} />
          </CardContent>
        </Card>
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} data-testid="btn-prev-atras">Atrás</Button>
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={onNext} data-testid="btn-prev-continuar">Continuar a confirmar</Button>
      </div>
    </div>
  );
}

/* ── Paso 4: confirmar y emitir ── */
function PasoConfirmar({ doc, onBack, reload, puedeEmitir }) {
  const [confirmar, setConfirmar] = useState(false);
  const [enviar, setEnviar] = useState(true);
  const [emitiendo, setEmitiendo] = useState(false);
  const [fechaDialogOpen, setFechaDialogOpen] = useState(false);
  const idemRef = useRef(`emit-${doc.id}-${Date.now()}`);

  const hoyCL = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
  const fechaDoc = (doc.fecha_emision || doc.emitido_at || hoyCL).slice(0, 10);
  const esFechaPasada = fechaDoc < hoyCL;

  const doEmitir = async () => {
    setEmitiendo(true);
    try {
      await emisionDteApi.emitir(doc.id, { confirmar: true, enviar_receptor: enviar, idempotency_key: idemRef.current });
      toast.success('Documento emitido. Contabilizando en Alegra…');
      await reload();
      setTimeout(() => { reload(); }, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo emitir el documento');
    } finally {
      setEmitiendo(false);
    }
  };

  const emitir = () => {
    if (esFechaPasada) { setFechaDialogOpen(true); return; }
    doEmitir();
  };

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Send className="w-4 h-4 text-indigo-500" /> Confirmar emisión</CardTitle></CardHeader>
      <CardContent className="space-y-4" data-testid="panel-confirmar">
        <ResumenDoc doc={doc} />
        {esFechaPasada && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2" data-testid="alerta-fecha-vencida">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>La fecha de emisión quedó en un día pasado (<b>{fmtFecha(fechaDoc)}</b>), anterior a hoy ({fmtFecha(hoyCL)}). Revisa que la fecha sea la correcta antes de emitir.</span>
          </div>
        )}
        <Separator />
        <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 space-y-3">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox checked={confirmar} onCheckedChange={(v) => setConfirmar(!!v)} data-testid="chk-confirmar" className="mt-0.5" />
            <span>Confirmo que los datos son correctos y autorizo la emisión. Esto consume un folio y no se puede deshacer (se corrige con una Nota de Crédito).</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={enviar} onCheckedChange={(v) => setEnviar(!!v)} data-testid="chk-enviar" /> Enviar el documento al correo del cliente al emitir
          </label>
        </div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack} disabled={emitiendo} data-testid="btn-conf-atras">Atrás</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={emitir} disabled={!confirmar || emitiendo || !puedeEmitir} data-testid="btn-emitir">
            {emitiendo ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Emitir documento
          </Button>
        </div>
      </CardContent>

      <Dialog open={fechaDialogOpen} onOpenChange={setFechaDialogOpen}>
        <DialogContent data-testid="dialog-fecha-vencida">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-5 h-5" /> Fecha de emisión en el pasado</DialogTitle>
            <DialogDescription>
              La fecha de emisión quedó en un día pasado (<b>{fmtFecha(fechaDoc)}</b>), anterior a hoy ({fmtFecha(hoyCL)}). ¿Deseas emitir el documento con esa fecha de todas formas?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFechaDialogOpen(false)} data-testid="btn-fecha-cancelar">Revisar la fecha</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => { setFechaDialogOpen(false); doEmitir(); }} data-testid="btn-fecha-confirmar">Emitir de todas formas</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ───────────────────────── Vista emitido ───────────────────────── */
function VistaEmitido({ doc, reload, puedeEmitir, esGerencia, puedeAnular }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <Card><CardContent className="p-5"><ResumenDoc doc={doc} /></CardContent></Card>
        <PanelAdjuntos doc={doc} reload={reload} puedeEmitir={puedeEmitir} />
        <PanelAuditoria doc={doc} />
      </div>
      <div className="space-y-4">
        <PanelRepresentacion doc={doc} />
        {(doc.tipo_dte === '33' || doc.tipo_dte === '34') && <PanelAnular doc={doc} puedeAnular={puedeAnular} />}
        {doc.tipo_dte === '61' && doc.anula_documento_id && <PanelReversaNC doc={doc} reload={reload} esGerencia={esGerencia} />}
        {doc.origen === 'externo' && !doc.proforma_id && <PanelAsociarProforma doc={doc} reload={reload} esGerencia={esGerencia} />}
        {doc.dte_proveedor && <PanelProveedor doc={doc} />}
        {doc.tipo_dte !== '52' && <PanelContabilizacion doc={doc} reload={reload} esGerencia={esGerencia} />}
        <PanelFactoring doc={doc} reload={reload} esGerencia={esGerencia} />
        <PanelEventosReceptor doc={doc} reload={reload} />
        <PanelReenviar doc={doc} reload={reload} />
      </div>
    </div>
  );
}

function PanelAnular({ doc, puedeAnular }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [anulando, setAnulando] = useState(false);
  const anulada = doc.estado === 'anulado_nc' || doc.situacion_comercial === 'anulada';
  const tieneNc = Boolean(doc.anulacion_nc_id);

  const anular = async () => {
    if (!motivo.trim()) { toast.error('Indica el motivo de la anulación'); return; }
    setAnulando(true);
    try {
      const { data } = await emisionDteApi.anular(doc.id, { confirmar: true, motivo: motivo.trim() });
      toast.success('Nota de Crédito borrador creada. Revísala y emítela.');
      setOpen(false);
      navigate(`/emision-dte/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo anular la factura');
    } finally {
      setAnulando(false);
    }
  };

  if (anulada) {
    return (
      <Card data-testid="panel-anular" className="border-gray-300 bg-gray-50">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Ban className="w-4 h-4 text-gray-500" /> Factura anulada</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p className="text-[12px] text-gray-600">Esta factura fue anulada mediante una Nota de Crédito.</p>
          {doc.anulacion_nc_id && (
            <Button size="sm" variant="outline" className="w-full text-red-700 border-red-300 hover:bg-red-50" onClick={() => navigate(`/emision-dte/${doc.anulacion_nc_id}`)} data-testid="btn-ver-nc">
              <Ban className="w-4 h-4 mr-1" /> Ver Nota de Crédito
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="panel-anular" className="border-red-200">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Ban className="w-4 h-4 text-red-600" /> Anular factura</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        {tieneNc ? (
          <>
            <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md p-2">Ya existe una <b>Nota de Crédito</b> de anulación para esta factura. Revísala o emítela.</p>
            <Button size="sm" variant="outline" className="w-full text-red-700 border-red-300 hover:bg-red-50" onClick={() => navigate(`/emision-dte/${doc.anulacion_nc_id}`)} data-testid="btn-ver-nc">
              <Ban className="w-4 h-4 mr-1" /> Ver Nota de Crédito
            </Button>
          </>
        ) : (
          <>
            <p className="text-[11px] text-muted-foreground">La anulación genera una <b className="text-red-600">Nota de Crédito</b> borrador por el 100% del documento. No copia Contrato, HES ni OC: la trazabilidad es por el folio de la factura.</p>
            <Button size="sm" className="w-full bg-red-600 hover:bg-red-700" onClick={() => setOpen(true)} disabled={!puedeAnular} data-testid="btn-abrir-anular">
              <Ban className="w-4 h-4 mr-1" /> Anular con Nota de Crédito
            </Button>
            {!puedeAnular && <p className="text-[11px] text-amber-700">Solo Administración/Gerencia y Finanzas pueden anular.</p>}
          </>
        )}
      </CardContent>
      <Dialog open={open} onOpenChange={(o) => !anulando && setOpen(o)}>
        <DialogContent data-testid="dialog-anular">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Ban className="w-5 h-5" /> Anular factura folio {doc.folio}</DialogTitle>
            <DialogDescription>Se creará una <b className="text-red-600">Nota de Crédito</b> borrador por el 100% del documento, ligada a esta factura. Indica el motivo (obligatorio).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Motivo de la anulación <span className="text-red-600">*</span></Label>
            <Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: error en el monto facturado, anulación por acuerdo comercial…" data-testid="anular-motivo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={anulando}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={anular} disabled={anulando || !motivo.trim()} data-testid="btn-confirmar-anular">
              {anulando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />} Crear Nota de Crédito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PanelReversaNC({ doc, reload, esGerencia }) {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const facturaId = doc.anula_documento_id;
  const yaRevertida = doc.estado === 'rechazado_sii';

  const abrir = async () => {
    setOpen(true); setPlan(null); setCargando(true);
    try {
      const { data } = await emisionDteApi.reversarNcRechazada({ nc_id: doc.id, factura_id: facturaId });
      setPlan(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo previsualizar la reversa');
      setOpen(false);
    } finally { setCargando(false); }
  };

  const ejecutar = async () => {
    setEjecutando(true);
    try {
      const { data } = await emisionDteApi.reversarNcRechazada({ nc_id: doc.id, factura_id: facturaId, dry_run: false, confirmar: true });
      toast.success(data.idempotente ? 'La reversa ya estaba aplicada.' : 'NC revertida: la factura volvió a quedar vigente.');
      setOpen(false);
      if (reload) reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo ejecutar la reversa');
    } finally { setEjecutando(false); }
  };

  if (yaRevertida) {
    return (
      <Card data-testid="panel-reversa-nc" className="border-gray-300 bg-gray-50">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Ban className="w-4 h-4 text-gray-500" /> NC rechazada por el SII</CardTitle></CardHeader>
        <CardContent className="text-[12px] text-gray-600">Esta Nota de Crédito fue marcada como <b>rechazada por el SII</b> y revertida: ya no anula a la factura, y se eliminó en Alegra.</CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="panel-reversa-nc" className="border-amber-300">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Revertir NC rechazada</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-[11px] text-muted-foreground">Usa esto SOLO si el SII <b>rechazó</b> esta Nota de Crédito (no quedó DOK) y no cumplió su objetivo. La elimina en Alegra y deja la <b>factura referenciada vigente</b> de nuevo. No toca proformas.</p>
        <Button size="sm" variant="outline" className="w-full text-amber-700 border-amber-300 hover:bg-amber-50" onClick={abrir} disabled={!esGerencia || !facturaId} data-testid="btn-abrir-reversa-nc">
          <AlertTriangle className="w-4 h-4 mr-1" /> Revisar reversa
        </Button>
        {!esGerencia && <p className="text-[11px] text-amber-700">Solo Administración/Gerencia puede revertir.</p>}
      </CardContent>
      <Dialog open={open} onOpenChange={(o) => !ejecutando && setOpen(o)}>
        <DialogContent data-testid="dialog-reversa-nc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-5 h-5" /> Revertir NC folio {doc.folio}</DialogTitle>
            <DialogDescription>Vista previa de los cambios. Nada se aplica hasta que confirmes.</DialogDescription>
          </DialogHeader>
          {cargando ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="w-4 h-4 animate-spin" /> Calculando…</div>
          ) : plan ? (
            <div className="space-y-2 text-sm">
              {plan.idempotente ? (
                <p className="text-[12px] text-gray-700 bg-gray-50 border rounded-md p-2">{plan.mensaje}</p>
              ) : (
                <>
                  <ul className="list-disc pl-5 space-y-1 text-[12px] text-gray-700">
                    {(plan.acciones || []).map((a, i) => <li key={i} data-testid={`reversa-accion-${i}`}>{a}</li>)}
                  </ul>
                  <p className="text-[11px] text-muted-foreground">{plan.proformas}</p>
                  {plan.preview_simula_alegra && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">Estás en PREVIEW: la parte de Alegra se simula (no toca el Alegra real).</p>}
                </>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={ejecutando}>Cancelar</Button>
            {plan && !plan.idempotente && (
              <Button className="bg-amber-600 hover:bg-amber-700" onClick={ejecutar} disabled={ejecutando} data-testid="btn-confirmar-reversa-nc">
                {ejecutando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />} Ejecutar reversa
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PanelRepresentacion({ doc }) {
  const [bajando, setBajando] = useState(false);
  const descargar = async () => {
    setBajando(true);
    try {
      const r = await emisionDteApi.representacion(doc.id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(TIPO_LABEL[doc.tipo_dte] || 'documento').replace(/\s+/g, '_')}_${doc.folio || 'sf'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      toast.error(await leerErrorBlob(err, 'No se pudo generar el PDF'));
    } finally {
      setBajando(false);
    }
  };
  return (
    <Card data-testid="panel-representacion" className="border-indigo-200">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" /> PDF del documento</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-[11px] text-muted-foreground">
          Representación impresa con fecha dd-mm-aaaa y vencimiento, sección de Referencias y total en palabras. Incluye el timbre electrónico cuando el documento tiene XML firmado.
        </p>
        <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={descargar} disabled={bajando} data-testid="btn-representacion-pdf">
          {bajando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Reemitir PDF
        </Button>
      </CardContent>
    </Card>
  );
}

function PanelProveedor({ doc }) {
  const prov = doc.dte_proveedor || {};
  const [bajando, setBajando] = useState('');
  const descargar = async (fmt) => {
    setBajando(fmt);
    try {
      const r = await emisionDteApi.documento(doc.id, fmt);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(TIPO_LABEL[doc.tipo_dte] || 'documento').replace(/\s+/g, '_')}_${doc.folio || 'sf'}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      toast.error(await leerErrorBlob(err, `No se pudo descargar el ${fmt.toUpperCase()}`));
    } finally {
      setBajando('');
    }
  };
  return (
    <Card data-testid="panel-proveedor" className={prov.es_simulacion ? 'border-amber-200' : 'border-emerald-200'}>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Cloud className="w-4 h-4 text-purple-600" /> Documento del proveedor (DTECore)</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Ambiente</span>
          {prov.es_simulacion
            ? <span data-testid="prov-simulacion" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold bg-amber-100 text-amber-800 border-amber-200"><ShieldAlert className="w-3 h-3" /> Simulación (no enviado al SII)</span>
            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold bg-emerald-100 text-emerald-800 border-emerald-200"><ShieldCheck className="w-3 h-3" /> Producción (enviado al SII)</span>}
        </div>
        <Row k="Folio asignado" v={prov.folio || '—'} />
        {prov.track_id && <Row k="Track ID (SII)" v={prov.track_id} />}
        {prov.estado_sii && <Row k="Estado PDF" v={prov.estado_sii} />}
        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="outline" className="flex-1" onClick={() => descargar('pdf')} disabled={bajando === 'pdf'} data-testid="btn-descargar-pdf">
            {bajando === 'pdf' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} PDF
          </Button>
          <Button size="sm" variant="outline" className="flex-1" onClick={() => descargar('xml')} disabled={bajando === 'xml'} data-testid="btn-descargar-xml">
            {bajando === 'xml' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} XML
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PanelContabilizacion({ doc, reload, esGerencia }) {
  const [loading, setLoading] = useState(false);
  const [verifLoading, setVerifLoading] = useState(false);
  const [reOpen, setReOpen] = useState(false);
  const [rePlan, setRePlan] = useState(null);
  const [reCargando, setReCargando] = useState(false);
  const [reEjecutando, setReEjecutando] = useState(false);
  const contabilizado = doc.estado_contable === 'contabilizado';
  const enError = doc.estado_contable === 'error_contabilizacion';
  const faltaProforma = doc.estado_contable === 'pendiente_proforma';
  const esperandoSii = doc.estado_contable === 'pendiente_sii';
  const rechazadoSii = doc.estado_contable === 'rechazado_sii';
  const reintentable = !contabilizado && !faltaProforma && !esperandoSii && !rechazadoSii;
  const verificar = async () => {
    setVerifLoading(true);
    try {
      const { data } = await emisionDteApi.verificarSii(doc.id);
      const ec = data?.estado_contable;
      if (ec === 'contabilizado' || ec === 'pendiente') toast.success('El SII aceptó el documento (DOK). Contabilizando en Alegra…');
      else if (ec === 'rechazado_sii') toast.error('El SII rechazó el documento. No se contabiliza.');
      else toast.info('El SII aún no entrega veredicto. Intenta de nuevo en unos minutos.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo consultar el estado en el SII');
    } finally {
      await reload();
      setVerifLoading(false);
    }
  };
  const run = async () => {
    setLoading(true);
    try {
      await emisionDteApi.contabilizar(doc.id);
      toast.success('Contabilizado en Alegra');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo contabilizar en Alegra');
    } finally {
      await reload();
      setLoading(false);
    }
  };
  const abrirRecontab = async () => {
    setReOpen(true); setRePlan(null); setReCargando(true);
    try {
      const { data } = await emisionDteApi.recontabilizar({ doc_id: doc.id });
      setRePlan(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo previsualizar la re-contabilización');
      setReOpen(false);
    } finally { setReCargando(false); }
  };
  const ejecutarRecontab = async (forzar = false) => {
    setReEjecutando(true);
    try {
      const { data } = await emisionDteApi.recontabilizar({ doc_id: doc.id, dry_run: false, confirmar: true, forzar });
      if (data.ok) {
        toast.success(`Re-contabilizado en Alegra. Folio Alegra: ${data.contab_nuevo?.folio_alegra}`);
        setReOpen(false);
        await reload();
      } else {
        toast.error(data.error || 'No se pudo re-contabilizar en Alegra');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo re-contabilizar en Alegra');
    } finally { setReEjecutando(false); }
  };
  return (
    <Card data-testid="panel-contabilizacion" className={enError ? 'border-red-200' : ''}>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-500" /> Contabilización (Alegra)</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between"><span className="text-muted-foreground">Estado</span><EstadoContableBadge estado={doc.estado_contable} /></div>
        {doc.folio_alegra && <Row k="Folio Alegra" v={doc.folio_alegra} />}
        {doc.contab_id && <Row k="N° registro" v={doc.contab_id} />}
        {doc.contab_simulado && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="contab-simulado">
            Contabilización <b>SIMULADA</b> (ambiente de pruebas): el folio en Alegra coincide con el del DTE. En producción se registra realmente.
          </p>
        )}
        {enError && doc.contab_error && (
          <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="contab-error">{doc.contab_error}</p>
        )}
        {faltaProforma && (
          <p className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded-md p-2" data-testid="contab-falta-proforma">
            Documento <b>Externo</b> sin proforma asociada. Asóciale una proforma (panel «Asociar proforma») para liberarlo y contabilizarlo en Alegra.
          </p>
        )}
        {esperandoSii && (
          <div data-testid="contab-esperando-sii" className="space-y-2">
            <p className="text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-md p-2">
              Esperando la confirmación del <b>SII</b>. El documento se contabiliza en Alegra <b>solo cuando quede DOK</b>. Se revisa automáticamente; puedes forzar la consulta ahora.
            </p>
            {doc.sii_estado && <Row k="Estado SII" v={doc.sii_estado} />}
            <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={verificar} disabled={verifLoading || !esGerencia} data-testid="btn-verificar-sii">
              {verifLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Clock className="w-4 h-4 mr-1" />} Consultar estado SII ahora
            </Button>
          </div>
        )}
        {rechazadoSii && (
          <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="contab-rechazado-sii">
            El <b>SII rechazó</b> este documento{doc.sii_estado ? ` (estado ${doc.sii_estado})` : ''}. No se contabiliza en Alegra. Debes corregir y volver a emitir.
          </p>
        )}
        {reintentable && (
          <p className="text-[11px] text-muted-foreground">Al emitir, el documento se contabiliza automáticamente en Alegra en segundo plano. Si quedó pendiente o con error, puedes reintentar aquí.</p>
        )}
        {reintentable && (
          <Button size="sm" className="w-full" onClick={run} disabled={loading || !esGerencia} data-testid="btn-contabilizar">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {enError ? 'Reintentar contabilización' : 'Contabilizar ahora'}
          </Button>
        )}
        {contabilizado && esGerencia && (
          <div className="pt-1 border-t border-dashed">
            <p className="text-[11px] text-muted-foreground mt-2 mb-1">¿El registro en Alegra fue eliminado? Vuelve a generarlo con el folio del DTE.</p>
            <Button size="sm" variant="outline" className="w-full text-indigo-700 border-indigo-300 hover:bg-indigo-50" onClick={abrirRecontab} data-testid="btn-abrir-recontabilizar">
              <Clock className="w-4 h-4 mr-1" /> Re-contabilizar en Alegra
            </Button>
          </div>
        )}
        {!esGerencia && !contabilizado && <p className="text-[11px] text-amber-700">Solo Finanzas/Gerencia puede contabilizar.</p>}
      </CardContent>
      <Dialog open={reOpen} onOpenChange={(o) => !reEjecutando && setReOpen(o)}>
        <DialogContent data-testid="dialog-recontabilizar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700"><Clock className="w-5 h-5" /> Re-contabilizar en Alegra · folio {doc.folio}</DialogTitle>
            <DialogDescription>Vista previa. Nada se aplica hasta que confirmes.</DialogDescription>
          </DialogHeader>
          {reCargando ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="w-4 h-4 animate-spin" /> Calculando…</div>
          ) : rePlan ? (
            <div className="space-y-2 text-sm">
              <ul className="list-disc pl-5 space-y-1 text-[12px] text-gray-700">
                {(rePlan.acciones || []).map((a, i) => <li key={i} data-testid={`recontab-accion-${i}`}>{a}</li>)}
              </ul>
              {rePlan.contab_previo?.contab_alegra_id && (
                <p className="text-[11px] text-muted-foreground">Reemplaza el registro anterior (eliminado) id {rePlan.contab_previo.contab_alegra_id}, folio {rePlan.contab_previo.folio_alegra}.</p>
              )}
              {rePlan.preview_simula_alegra && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">Estás en PREVIEW: la parte de Alegra se simula (no toca el Alegra real).</p>}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReOpen(false)} disabled={reEjecutando}>Cancelar</Button>
            {rePlan && (
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => ejecutarRecontab(false)} disabled={reEjecutando} data-testid="btn-confirmar-recontabilizar">
                {reEjecutando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Clock className="w-4 h-4 mr-1" />} Re-contabilizar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PanelAsociarProforma({ doc, reload, esGerencia }) {
  const [proformas, setProformas] = useState([]);
  const [sel, setSel] = useState('');
  const [loading, setLoading] = useState(false);
  const [cargando, setCargando] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await emisionDteApi.listarProformas({ estado: 'aprobada' });
        const rut = (doc.receptor?.rut || '').replace(/[.-]/g, '').toLowerCase();
        const todas = data?.proformas || [];
        const lista = rut ? todas.filter((p) => (p.cliente_rut || '').replace(/[.-]/g, '').toLowerCase() === rut) : todas;
        setProformas(lista.length ? lista : todas);
      } catch { /* noop */ } finally { setCargando(false); }
    })();
  }, [doc.receptor?.rut]);
  const asociar = async () => {
    if (!sel) { toast.error('Selecciona una proforma'); return; }
    setLoading(true);
    try {
      await emisionDteApi.asociarProforma(doc.id, sel);
      toast.success('Proforma asociada. Contabilizando en Alegra…');
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo asociar la proforma');
    } finally { setLoading(false); }
  };
  return (
    <Card data-testid="panel-asociar-proforma" className="border-orange-200">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Link2 className="w-4 h-4 text-orange-500" /> Asociar proforma</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-[11px] text-muted-foreground">Documento externo sin proforma. Asóciale una (enlace interno) para liberarlo y contabilizarlo en Alegra. No modifica el XML emitido al SII.</p>
        {(doc.referencias?.codigo_contrato || doc.referencias?.ot_numero) && (
          <p className="text-[11px] text-muted-foreground">Referencia del XML: <b>{doc.referencias?.codigo_contrato || `OT ${doc.referencias?.ot_numero}`}</b></p>
        )}
        <SearchableCombobox
          items={proformas.map((p) => ({ value: p.id, label: `#${p.numero_proforma} · OT ${p.ot_numero}`, subLabel: p.cliente_nombre || p.cliente_rut }))}
          value={sel}
          onChange={(it) => setSel(it ? it.value : '')}
          placeholder={cargando ? 'Cargando proformas…' : 'Buscar proforma aprobada…'}
          disabled={cargando || !esGerencia}
          testid="asociar-proforma-combo"
        />
        <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700" onClick={asociar} disabled={loading || !sel || !esGerencia} data-testid="btn-asociar-proforma">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />} Asociar y contabilizar
        </Button>
        {!esGerencia && <p className="text-[11px] text-amber-700">Solo Finanzas/Gerencia puede asociar.</p>}
      </CardContent>
    </Card>
  );
}

function PanelFactoring({ doc, reload, esGerencia }) {
  const f = doc.factoring || {};
  const [form, setForm] = useState({
    cedible: !!f.cedible, estado: f.estado || 'no_cedible', empresa: f.empresa || '',
    fecha_cesion: (f.fecha_cesion || '').slice(0, 10), monto_cedido: f.monto_cedido || '', responsable: f.responsable || '', observaciones: f.observaciones || '',
  });
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    setSaving(true);
    try {
      await emisionDteApi.factoring(doc.id, {
        cedible: form.cedible, estado: form.estado, empresa: form.empresa,
        fecha_cesion: form.fecha_cesion || null, monto_cedido: Number(form.monto_cedido) || 0,
        responsable: form.responsable, observaciones: form.observaciones,
      });
      toast.success('Factoring actualizado');
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar el factoring');
    } finally { setSaving(false); }
  };
  return (
    <Card data-testid="panel-factoring">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Banknote className="w-4 h-4 text-purple-500" /> Cesión / Factoring</CardTitle></CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        <div className="flex items-center justify-between"><span className="text-muted-foreground">Estado actual</span><FactoringBadge factoring={doc.factoring} /></div>
        <label className="flex items-center gap-2 text-xs"><Checkbox checked={form.cedible} onCheckedChange={(v) => setForm({ ...form, cedible: !!v })} data-testid="fac-cedible" /> Documento cedible</label>
        <div>
          <Label className="text-[10px]">Estado de cesión</Label>
          <select className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} data-testid="fac-estado">
            {FACTORING_ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
        </div>
        <div><Label className="text-[10px]">Empresa de factoring</Label><Input className="h-9" value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} data-testid="fac-empresa" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[10px]">Fecha cesión</Label><Input type="date" className="h-9" value={form.fecha_cesion} onChange={(e) => setForm({ ...form, fecha_cesion: e.target.value })} /></div>
          <div><Label className="text-[10px]">Monto cedido</Label><NumberInput allowDecimal={false} allowNegative={false} className="h-9" value={form.monto_cedido} onChange={(clean) => setForm({ ...form, monto_cedido: clean })} /></div>
        </div>
        <div><Label className="text-[10px]">Responsable</Label><Input className="h-9" value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} /></div>
        <div><Label className="text-[10px]">Observaciones</Label><Textarea rows={2} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>
        <Button size="sm" className="w-full bg-purple-600 hover:bg-purple-700" onClick={guardar} disabled={saving || !esGerencia} data-testid="btn-guardar-factoring">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Guardar factoring
        </Button>
        {!esGerencia && <p className="text-[11px] text-amber-700">Solo Finanzas/Gerencia gestiona factoring.</p>}
      </CardContent>
    </Card>
  );
}

function PanelEventosReceptor({ doc, reload }) {
  const [tipo, setTipo] = useState('acuse_recibo');
  const [obs, setObs] = useState('');
  const [saving, setSaving] = useState(false);
  const registrar = async () => {
    setSaving(true);
    try {
      await emisionDteApi.eventoReceptor(doc.id, { tipo, observaciones: obs });
      toast.success('Evento registrado');
      setObs('');
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo registrar el evento');
    } finally { setSaving(false); }
  };
  const eventos = doc.eventos_receptor || [];
  return (
    <Card data-testid="panel-eventos-receptor">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-teal-500" /> Eventos del receptor</CardTitle></CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        {eventos.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-auto">
            {eventos.map((e, i) => (
              <div key={i} className="text-xs border-l-2 border-teal-300 pl-2">
                <span className="font-medium">{EVENTO_RECEPTOR.find((x) => x.value === e.tipo)?.label || e.tipo}</span>
                <span className="text-muted-foreground"> · {fmtFecha(e.fecha)}</span>
                {e.observaciones && <p className="text-muted-foreground">{e.observaciones}</p>}
              </div>
            ))}
          </div>
        )}
        <div>
          <Label className="text-[10px]">Tipo de evento</Label>
          <select className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="evento-tipo">
            {EVENTO_RECEPTOR.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
        </div>
        <Textarea rows={2} placeholder="Observaciones (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} data-testid="evento-obs" />
        <Button size="sm" variant="outline" className="w-full" onClick={registrar} disabled={saving} data-testid="btn-registrar-evento">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Registrar evento
        </Button>
      </CardContent>
    </Card>
  );
}

function PanelReenviar({ doc, reload }) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const pendiente = doc.email_pendiente || !doc.email_enviado;
  const reenviar = async () => {
    setLoading(true);
    try {
      const body = email.trim() ? { emails: [email.trim()] } : {};
      const r = await emisionDteApi.reenviar(doc.id, body);
      toast.success(r.data?.simulado ? 'Reenvío simulado (ambiente de pruebas)' : `Enviado a ${(r.data?.to || []).join(', ')}`);
      setEmail('');
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo reenviar');
    } finally { setLoading(false); }
  };
  return (
    <Card data-testid="panel-reenviar" className={pendiente ? 'border-amber-200' : ''}>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Send className="w-4 h-4 text-indigo-500" /> Envío al receptor (XML + PDF)</CardTitle></CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Correo enviado</span>
          {doc.email_enviado
            ? <span className="inline-flex items-center gap-1 text-emerald-600 font-medium" data-testid="reenviar-estado"><CheckCircle2 className="w-3.5 h-3.5" /> Sí</span>
            : <span className="inline-flex items-center gap-1 text-amber-600 font-medium" data-testid="reenviar-estado"><AlertTriangle className="w-3.5 h-3.5" /> Pendiente</span>}
        </div>
        {pendiente && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="reenviar-aviso-pendiente">
            El documento quedó emitido pero el XML aún no se envió (el cliente no tiene correo o falló el envío). Indica un correo y reprocesa.
          </p>
        )}
        <div>
          <Label className="text-[10px]">Correo de destino (opcional)</Label>
          <Input className="h-9" type="email" placeholder="Dejar vacío para usar el del cliente" value={email}
            onChange={(e) => setEmail(e.target.value)} data-testid="reenviar-email" />
          <p className="text-[10px] text-muted-foreground mt-1">Copia fija a dte@chirinosteel.com</p>
        </div>
        <Button size="sm" variant="outline" className="w-full" onClick={reenviar} disabled={loading} data-testid="btn-reenviar">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} {pendiente ? 'Reprocesar envío' : 'Reenviar al cliente'}
        </Button>
      </CardContent>
    </Card>
  );
}

function PanelAdjuntos({ doc, reload, puedeEmitir }) {
  const [subiendo, setSubiendo] = useState(false);
  const [tipo, setTipo] = useState('respaldo');
  const fileRef = useRef(null);
  const adjuntos = (doc.adjuntos || []).filter((a) => !a.is_deleted);

  const subir = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipo);
      await emisionDteApi.subirAdjunto(doc.id, fd);
      toast.success('Adjunto subido');
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo subir el adjunto');
    } finally {
      setSubiendo(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const descargar = async (adj) => {
    try {
      const r = await emisionDteApi.descargarAdjunto(doc.id, adj.id);
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error('No se pudo descargar'); }
  };

  const eliminar = async (adj) => {
    if (!window.confirm(`¿Eliminar el adjunto "${adj.nombre}"?`)) return;
    try {
      await emisionDteApi.eliminarAdjunto(doc.id, adj.id);
      toast.success('Adjunto eliminado');
      await reload();
    } catch { toast.error('No se pudo eliminar'); }
  };

  return (
    <Card data-testid="panel-adjuntos">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Paperclip className="w-4 h-4 text-indigo-500" /> Adjuntos comerciales</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        {adjuntos.length === 0 && <p className="text-xs text-muted-foreground">Sin adjuntos. Sube OC del cliente, HES, guías, respaldos.</p>}
        {adjuntos.map((a) => (
          <div key={a.id} className="flex items-center justify-between border rounded-md px-2.5 py-1.5" data-testid={`adjunto-${a.id}`}>
            <div className="min-w-0">
              <p className="truncate font-medium text-xs">{a.nombre}</p>
              <p className="text-[10px] text-muted-foreground">{a.tipo} · {Math.round((a.size || 0) / 1024)} KB</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => descargar(a)} data-testid={`adjunto-descargar-${a.id}`}><Download className="w-3.5 h-3.5" /></Button>
              {puedeEmitir && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => eliminar(a)} data-testid={`adjunto-eliminar-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        ))}
        {puedeEmitir && (
          <div className="flex items-end gap-2 pt-1">
            <div className="flex-1">
              <Label className="text-[10px]">Tipo</Label>
              <select className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="adjunto-tipo">
                <option value="respaldo">Respaldo</option>
                <option value="oc_cliente">OC del cliente</option>
                <option value="hes">HES</option>
                <option value="guia">Guía de despacho</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <input ref={fileRef} type="file" className="hidden" onChange={subir} data-testid="adjunto-input" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={subiendo} data-testid="btn-subir-adjunto">
              {subiendo ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Subir
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PanelAuditoria({ doc }) {
  const [open, setOpen] = useState(false);
  const aud = doc.auditoria || [];
  return (
    <Card data-testid="panel-auditoria">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4 text-indigo-500" /> Trazabilidad ({aud.length})</CardTitle>
        <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid="btn-toggle-auditoria">{open ? 'Ocultar' : 'Ver todo'}</Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {(open ? aud : aud.slice(-5)).map((e) => (
            <div key={e.id} className="flex gap-2 text-xs border-l-2 border-indigo-200 pl-2">
              <div className="flex-1">
                <span className="font-medium">{e.evento}</span>
                <span className="text-muted-foreground"> · {e.usuario_nombre || e.usuario}</span>
                {e.observaciones && <p className="text-muted-foreground">{e.observaciones}</p>}
              </div>
              <span className="text-muted-foreground whitespace-nowrap">{fmtFecha(e.at)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ───────────────────────── Helpers UI ───────────────────────── */
function ResumenDoc({ doc }) {
  if (!doc) return null;
  const rec = doc.receptor || {};
  const t = doc.totales || {};
  const esExenta = doc.tipo_dte === '34';
  return (
    <div className="space-y-4" data-testid="resumen-doc">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Emisor</p>
          <p className="font-bold">CHIRINO STEEL</p>
          <p className="text-xs text-muted-foreground">76.738.421-1</p>
        </div>
        <div className="text-right border-2 border-red-300 rounded-md px-3 py-1.5">
          <p className="text-[10px] font-bold text-red-600">{TIPO_LABEL[doc.tipo_dte]}</p>
          <p className="text-xs">Folio {doc.folio || '—'}</p>
          <p className="text-[10px] text-muted-foreground">Emisión {fmtFecha(doc.emitido_at || doc.fecha_emision || doc.created_at)}</p>
        </div>
      </div>
      <Separator />
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Receptor</p>
        <p className="font-semibold text-sm">{rec.razon_social || '—'}</p>
        <p className="text-xs text-muted-foreground">{rec.rut} · {rec.giro || 'sin giro'}</p>
        <p className="text-xs text-muted-foreground">{[rec.direccion, rec.comuna, rec.ciudad].filter(Boolean).join(', ')}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="border-b"><tr className="text-left text-muted-foreground"><th className="py-1">Descripción</th><th className="py-1 text-right">Cant.</th><th className="py-1 text-right">P. Unit</th><th className="py-1 text-right">Total</th></tr></thead>
          <tbody>
            {(doc.items || []).map((it, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1.5">{it.descripcion || '—'}{it.exento && <span className="ml-1 text-[9px] text-amber-600">(exento)</span>}</td>
                <td className="py-1.5 text-right">{it.cantidad} {it.unidad}</td>
                <td className="py-1.5 text-right font-mono">{fmt(it.precio_unitario)}</td>
                <td className="py-1.5 text-right font-mono">{fmt(Math.max(0, (it.cantidad || 0) * (it.precio_unitario || 0) - (it.descuento || 0)))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <div className="w-48 space-y-1 text-sm">
          <Row k="Neto" v={fmt(t.neto)} />
          {t.exento > 0 && <Row k="Exento" v={fmt(t.exento)} />}
          {!esExenta && <Row k={`IVA (${t.iva_porcentaje || 19}%)`} v={fmt(t.iva)} />}
          <Separator />
          <Row k="Total" v={fmt(t.total)} bold />
        </div>
      </div>
      {doc.total_en_palabras && (
        <p className="text-xs font-semibold text-gray-700" data-testid="resumen-total-palabras">{doc.total_en_palabras}</p>
      )}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px]" data-testid="resumen-condicion-pago">
        {doc.condicion_pago_texto && <span className="text-muted-foreground">Condición de pago: <b className="text-gray-700">{doc.condicion_pago_texto}</b></span>}
        {doc.fecha_vencimiento && <span className="text-muted-foreground">Vence: <b className="text-gray-700">{fmtFecha(doc.fecha_vencimiento)}</b></span>}
      </div>
      {(doc.referencias_detalle?.length > 0) && (
        <div data-testid="resumen-referencias">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Referencias</p>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="border-b"><tr className="text-left text-muted-foreground">
                <th className="py-1">Docto. Ref.</th><th className="py-1">Folio</th><th className="py-1">Fecha</th><th className="py-1">Razón</th>
              </tr></thead>
              <tbody>
                {doc.referencias_detalle.map((r, i) => (
                  <tr key={i} className="border-b last:border-0" data-testid={`referencia-row-${i}`}>
                    <td className="py-1">{r.tipo} ({r.codigo})</td>
                    <td className="py-1 font-mono">{r.folio}</td>
                    <td className="py-1">{fmtFecha(r.fecha)}</td>
                    <td className="py-1">{r.razon}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {doc.observaciones && <p className="text-[11px] text-muted-foreground italic">{doc.observaciones}</p>}
    </div>
  );
}

function Row({ k, v, bold }) {
  return <div className={`flex justify-between ${bold ? 'font-bold text-base' : ''}`}><span className="text-muted-foreground">{k}</span><span className="font-mono">{v}</span></div>;
}
function Dato({ k, v, warn }) {
  return <div><span className="text-[10px] uppercase text-muted-foreground block">{k}</span><span className={`text-sm ${warn ? 'text-amber-600' : ''}`}>{v || '— (falta)'}</span></div>;
}
function Campo({ label, v, onChange, testid, locked, hint }) {
  return (
    <div>
      <Label className="text-[10px]">{label}</Label>
      <Input className={`h-9 ${locked ? 'bg-muted/60 cursor-not-allowed' : ''}`} value={v || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={locked} readOnly={locked} />
      {locked && hint && <p className="text-[9px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}
